import React, { useEffect, useState, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'

import Redirect from '../../components/redirect'
import Intro from '../../components/intro'
import DonationType from '../../components/donation-type'
import Pricepoints from '../../components/pricepoints'
import GiftAid from '../../components/gift-aid'
import YourDetails from '../../components/your-details'
import BillingAddress from '../../components/billing-address'
import KeepingInTouch from '../../components/keeping-in-touch'
import Message from '../../components/message'
import SubmitButtons from '../../components/submit-buttons'

import initialValues from '../../data/initialValues'
import validation from '../../validation/payment'
import DonationFormContext from '../../context/donation'
import { setSS, getSS, cleanObject, googleAnalyticsClientID } from '../../helpers'
import { createDonation, saveDonation, getTitles } from '../../api'

const Homepage: React.FC = (props: any) => {

  const donationForm = useContext(DonationFormContext)
  const navigate = useNavigate()
  const params = useParams()
  const [ dataLayerInitialPush, setDataLayerInitialPush ] = useState(getSS('dataLayerInitialPush'))
  const [ homepage, setHomepage ] = useState(initialValues.homepage)
  const [ sessionId, setSessionId ] = useState('')
  const [ titles, setTitles ] = useState({})
  const [ submittingPayPal, setSubmittingPayPal ] = useState(false)

  const appeal = (window as any).KEW.collections.appeal[0]
  const url = params.url

  useEffect(() => {
    if (!appeal.status) {
      setTimeout(function() {
        if (appeal.form_redirect) {
            window.location.href = appeal.form_redirect
        } else {
            window.location.href = 'https://support.kew.org/';
        }
      }, 5000)
    }
  }, [appeal])

  useEffect(() => {

    document.body.classList.add('landing')
    document.body.style.backgroundImage = `url(${appeal.landing_page_background})`
    if (getSS('homepage')) {
      setHomepage(getSS('homepage'));
    }
    if (!initialValues.homepage.amount) {
      initialValues.homepage.amount = appeal.single_pp2
    }
    if (!appeal.single_active) {
      initialValues.homepage.payment_type = 'Regular'
      initialValues.homepage.amount = appeal.regular_pp2
    }
    if (donationForm.paramAmount) {
      initialValues.homepage.amount = donationForm.paramAmount
    }
    if (donationForm.paramType) {
      let type = donationForm.paramType.charAt(0).toUpperCase() + donationForm.paramType.slice(1)
      initialValues.homepage.payment_type = type
    }
    if (!dataLayerInitialPush) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          items: [{
            item_name: `Donation - ${appeal.name}`,
            item_id: appeal.campaigncode,
            price: initialValues.homepage.amount,
            item_brand: 'Kew',
            item_category: `${initialValues.homepage.payment_type === 'Single' ? 'single' : 'monthly'}`,
            quantity: 1
          }]
        }
      });
      setSS('dataLayerInitialPush', true)
      setDataLayerInitialPush(true)
    }
  }, [donationForm.paramAmount, donationForm.paramType, appeal.landing_page_background, appeal.single_pp2, appeal.regular_pp2, appeal.single_active, dataLayerInitialPush, appeal.name, appeal.campaigncode])

  useEffect(() => {

    function parseResponseHeaders(headerStr: any) {
      var headers:any = [];
      if (!headerStr) {
        return headers;
      }
      var headerPairs = headerStr.split('\u000d\u000a');
      for (let i = 0; i < headerPairs.length; i++) {
        let headerPair = headerPairs[i];
        var index = headerPair.indexOf(': ');
        if (index > 0) {
          var key = headerPair.substring(0, index);
          var val = headerPair.substring(index + 2);
          headers[key] = val
        }
      }
      return headers;
    }
    if (!sessionId) {
      const d = new Date();
      d.setTime(d.getTime() + 2*60*60*1000);
      const request = new XMLHttpRequest();
      request.open("HEAD", "/admin", true);
      request.send();
      request.onreadystatechange = function() {
        if (this.readyState === this.HEADERS_RECEIVED) {
          const headers = request.getAllResponseHeaders();
          setSessionId(parseResponseHeaders(headers)._sepia_auth_sessionid);
          document.cookie = '_sepia_creator_auth='+ parseResponseHeaders(headers)._sepia_auth_sessiontoken +'; expires=' + d.toUTCString() + '; path=/';
        }
      }
    }

  }, [sessionId])

  useEffect(() => {
    const loadTitles = async () => {
      let titles = await getTitles()
      setTitles(titles)
    }
    loadTitles()
  }, [])

  const braintreePayPalInit = (donation: any) => {

    const paypalButton: any = document.querySelector('#paypal-button');
    // PayPal
    // Create a client.
    (window as any).braintree.client.create({
        authorization: donation.payment_token
    }, function(clientErr: any, clientInstance: any) {

      // Stop if there was a problem creating the client.
      // This could happen if there is a network error or if the authorization
      // is invalid.
      if (clientErr) {
          console.error('Error creating client:', clientErr);
          return;
      }

      // Create a PayPal component.
      (window as any).braintree.paypal.create({
          client: clientInstance
      }, function(paypalErr: any, paypalInstance: any) {

        // Stop if there was a problem creating PayPal.
        // This could happen if there was a network error or if it's incorrectly
        // configured.
        if (paypalErr) {
            console.error('Error creating PayPal:', paypalErr);
            return;
        }

        // Because tokenization opens a popup, this has to be called as a result of
        // customer action, like clicking a button—you cannot call this at any time.

        if (!submittingPayPal) {

          paypalButton.addEventListener('click', function() {

            paypalInstance.tokenize({
                flow: 'checkout',
                amount: donation.amount,
                currency: 'GBP'
            }, async function(tokenizeErr: any, payload: any) {

              // Stop if there was an error.
              if (tokenizeErr) {
                  setSubmittingPayPal(true)
                  if (tokenizeErr.type !== 'CUSTOMER') {
                      console.error('Error tokenizing:', tokenizeErr);
                      if (tokenizeErr.code === 'PAYPAL_POPUP_OPEN_FAILED') {
                        alert('It looks like pop-up windows are disabled for this website. These need to be allowed for payment via PayPal - please enable them or try a different payment method.')
                      }
                  }
                  // hide loading
                  donationForm.hideLoading()
                  setSubmittingPayPal(false)
                  return false;
              }

              // NOTE: payment method nonces expire after 3 hours
              donation.payment_method_token_or_nonce = payload.nonce;

              // Update the donation record attempting to process the payment
              donation.process_payment = true;
              if (donation.optins_giftaid) {
                donation.giftaid_amount = (donation.amount*0.25).toFixed(2);
              }

              const savedDonation: any = await saveDonation(donation)
              console.log(savedDonation);
              if (savedDonation?.status === 200) {
                setSS('donation', savedDonation.data);
                donationForm.hideLoading();
                navigate(`/donate/${url}/thanks`)
              } else {
                alert('something went wrong saving the donation!')
                return
              }
            });

          }, { once: true, passive: true } );
          setSubmittingPayPal(true)
          paypalButton.click()
        }
      });
    });

  }

  const onSubmit = async (data:any) => {
    console.log(data);
    setSS('homepage', data)
    donationForm.showLoading()

    delete data.address_search
    data.appeal_id = appeal.id
    data.appeal_name = appeal.name
    data.ga_document_host = (window as any).location.hostname
    data.ga_tracking_id = (window as any).KEW.globals.ga_tracking_id
    data.gtm_container_id = (window as any).KEW.globals.gtm_container_id
    data.initial_location = (window as any).location.href
    data.session_id = sessionId
    data.ga_client_id = googleAnalyticsClientID()
    data.optins_post = !data.optins_post
    data.optins_phone = !data.optins_phone
    data.email = data.email.toLowerCase()

    if (!data.public_id) { // handle initial creation
      const createdDonation: any = await createDonation(data)
      if (createdDonation?.status !== 201) {
        alert('something went wrong creating the donation!')
        donationForm.hideLoading()
        return
      }
      data = createdDonation.data
      console.log(data);
    }

    // save with process_payment set to false (this generates a new payment_token for braintree)
    data.process_payment = false
    const savedDonation: any = await saveDonation(data)
    if (savedDonation?.status === 200) {
      data = savedDonation.data
      setSS('donation', data);

      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          payment_type: data.payment_method,
          items: [{
            item_name: `Donation - ${appeal.name}`,
            item_id: `${appeal.campaigncode}`,
            price: data.amount,
            item_brand: 'Kew',
            item_category: `${data.payment_type === 'Single' ? 'single' : 'monthly'}`,
            quantity: 1
          }]
        }
      });
      if (data.payment_method === 'PayPal') {
        braintreePayPalInit(data)
      }

      if (data.payment_method === 'CreditCard') {
        donationForm.hideLoading()
        navigate(`/donate/${url}/payment`)
      }
      if (data.payment_method === 'DirectDebit') {
        donationForm.hideLoading()
        navigate(`/donate/${url}/payment_regular`)
      }
    } else {
      alert('something went wrong saving the donation!')
      donationForm.hideLoading()
      return
    }
  }

  return (
    <>
      { !appeal?.status &&
        <Redirect />
      }
      { appeal?.status &&
        <Formik
          initialValues={ homepage }
          validationSchema={ validation.homepage }
          enableReinitialize={true}
          // validateOnChange={false}
          // validateOnBlur={true}
          onSubmit={ onSubmit }>
            {(props: any) => {
              props.errors = cleanObject(props.errors)
              return (
                <>
                  <Form name="donationForm">
                    <Intro {...props} />
                    <div className="row intro-divider"></div>
                    <DonationType {...props} />
                    <Pricepoints {...props} />
                    <GiftAid {...props} />
                    <YourDetails {...props} titles={titles} />
                    <BillingAddress {...props} />
                    <KeepingInTouch {...props} />
                    { appeal.show_message_field &&
                      <Message {...props} />
                    }
                    <SubmitButtons {...props} />
                  </Form>
                </>
              )
            }}
        </Formik>
      }
    </>
  )
}

export default Homepage